import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { space, tokPx } from '@sunwebgroup/ui-components'
import { focusOutline, Icon, IconGlyph, ListItem, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'
import { createDataLayerEvent } from './helpers/CreateDataLayerEvent'

interface ItemProps extends React.PropsWithChildren {
  title: string
  Glyph: IconGlyph
  elementDL: string
}

const ButtonWrapper = styled.button`
  ${focusOutline()}
  border-radius: ${(tokPx('primitives_corner_radius_m'))};
`

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_8')};
`
const IconChevron = styled(Icon)`
  margin-left: auto;
`
const Title = styled(Typography)`
  text-align: left;
`
const SheetContent = styled(Sheet.Content)`
    & > *:first-child {
      margin-top: 0;
      padding-top: 0;
    }
`

export function Item({ children, title, Glyph, elementDL }: ItemProps) {
  const intl = useIntl()
  const [isOpen, setOpen] = React.useState(false)
  const { sendEvent } = useAnalytics()

  const handleOnOpenChange = () => {
    const nextOpen = !isOpen
    sendEvent(createDataLayerEvent({ element: elementDL, isOpen: nextOpen }))
    setOpen(nextOpen)
  }

  return (
    <Sheet.Root open={isOpen} onOpenChange={handleOnOpenChange}>
      <Sheet.Trigger>
        <ButtonWrapper>
          <ListItem variant="outlined">
            <ItemRow>
              <Icon glyph={Glyph as IconGlyph} size={32} />
              <Title $variant="body-l-bold-tight">{title}</Title>
              <IconChevron glyph="ChevronRight" size={20} />
            </ItemRow>
          </ListItem>
        </ButtonWrapper>
      </Sheet.Trigger>
      <Sheet.Body side="right">
        <Sheet.Header
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={title}
        />
        <SheetContent>
          {children}
        </SheetContent>
      </Sheet.Body>
    </Sheet.Root>
  )
}
